import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { KitSummary as KitSummaryCounts, type KitMissingBlock } from './kitSlots';
import { blockedCount, KitBlockedLine } from './KitBlockedLine';
import { kitMessages } from './kitMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitSummaryProps {
  counts: KitSummaryCounts;
  /** The slots that "Make the N missing ones" makes; 0 hides the button. */
  missingCount: number;
  /** The not-made parts that the button leaves out, by reason (absent: none). */
  missingBlocks?: ReadonlyMap<KitMissingBlock, number>;
  missingCost: number;
  creditsLeft: number | null;
  canAct: boolean;
  isMaking: boolean;
  onMakeMissing: () => void;
}

/** The share of a bar part, in percent. */
const share = (part: number, total: number): string => `${total === 0 ? 0 : Math.round((part / total) * 100)}%`;

const NO_BLOCKS: ReadonlyMap<KitMissingBlock, number> = new Map();

/**
 * The summary of the Kit (design assets-rethink b1 `.kit-sum`): "9 of 10 kit parts made", the bar, the state
 * chips, and "Make the N missing ones" with its cost. "N wait for you" counts only the amber manual
 * steps (Spotify Canvas, Apple Music motion artwork), not the banners.
 */
const KitSummaryComponent: React.FC<KitSummaryProps> = ({
  counts,
  missingCount,
  missingCost,
  creditsLeft,
  canAct,
  isMaking,
  onMakeMissing,
  missingBlocks,
}) => {
  const blocks = missingBlocks ?? NO_BLOCKS;
  const blocked = blockedCount(blocks);
  const showButton = canAct && missingCount > 0;
  const intl = useIntl();
  return (
    <div className={styles.summary} data-testid="kit-summary">
      <div className={styles.meter}>
        <span className={styles.eyebrow}>{intl.formatMessage(kitMessages.summaryEyebrow)}</span>
        <b className={styles.made}>
          {intl.formatMessage(kitMessages.summaryMade, { made: counts.made, total: counts.total })}
        </b>
        <div className={styles.bar} aria-hidden="true">
          {/* The widths are the data: the share of each state of the slots. */}
          <i style={{ width: share(counts.made, counts.total) }} />
          <i className={styles.barWork} style={{ width: share(counts.making, counts.total) }} />
          <i className={styles.barBad} style={{ width: share(counts.failed, counts.total) }} />
        </div>
      </div>
      <div className={styles.chips}>
        {counts.making > 0 && (
          <span className={`${styles.st} ${styles.stWork}`}>
            {intl.formatMessage(kitMessages.summaryMaking, { count: counts.making })}
          </span>
        )}
        {counts.failed > 0 && (
          <span className={`${styles.st} ${styles.stBad}`}>
            {intl.formatMessage(kitMessages.summaryFailed, { count: counts.failed })}
          </span>
        )}
        {counts.notMade > 0 && (
          <span className={styles.st}>{intl.formatMessage(kitMessages.summaryNotMade, { count: counts.notMade })}</span>
        )}
        {counts.waitForYou > 0 && (
          <span className={`${styles.st} ${styles.stWarn}`} data-testid="kit-waits">
            {intl.formatMessage(kitMessages.summaryWaits, { count: counts.waitForYou })}
          </span>
        )}
      </div>
      <span className={styles.grow} />
      {!showButton && blocked > 0 && <KitBlockedLine blocks={blocks} />}
      {showButton && (
        <div className={styles.makeCol} data-testid="kit-make-missing">
          <SMLButton
            variant="primary"
            onClick={onMakeMissing}
            disabled={isMaking}
            icon={<FontAwesomeIcon icon={faSparkles} />}
          >
            {intl.formatMessage(blocked > 0 ? kitMessages.makeMissingNow : kitMessages.makeMissing, {
              count: missingCount,
            })}
          </SMLButton>
          <span className={styles.cost}>
            <MissingCostLine cost={missingCost} left={creditsLeft} />
          </span>
          <KitBlockedLine blocks={blocks} />
        </div>
      )}
    </div>
  );
};

export const KitSummary = TypedMemo(KitSummaryComponent);

/**
 * The cost line under "Make the N missing ones": free images, or the credits, the balance and what
 * the click leaves for reels and videos (QA finding F30). A balance that is too small leaves 0.
 */
const MissingCostLineComponent: React.FC<{ cost: number; left: number | null }> = ({ cost, left }) => {
  const intl = useIntl();
  if (cost === 0) {
    return <>{intl.formatMessage(kitMessages.makeMissingFree)}</>;
  }
  if (left === null) {
    return <>{intl.formatMessage(kitMessages.makeMissingCost, { cost, left: '∞' })}</>;
  }
  return (
    <>
      {intl.formatMessage(kitMessages.makeMissingCostLeaves, {
        cost,
        left: intl.formatNumber(left),
        after: intl.formatNumber(Math.max(0, left - cost)),
      })}
    </>
  );
};

const MissingCostLine = TypedMemo(MissingCostLineComponent);
